import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import './PricingSection.css';

const PricingSection = ({ 
  pricing, 
  onPricingChange, 
  category, 
  onUpgradeSelect,
  isEditMode = false 
}) => {
  const { t } = useTranslation();
  const [priceInput, setPriceInput] = useState(pricing?.price?.toString() || '');
  const [negotiableActive, setNegotiableActive] = useState(pricing?.negotiable || false);
  const [tradeActive, setTradeActive] = useState(pricing?.acceptTrade || false);

  const formatPrice = (price) => {
    if (!price && price !== 0) return t('pricingSection.format.priceZero');
    
    const priceNumber = parseFloat(price) || 0;
    const formattedPrice = new Intl.NumberFormat('fr-CM', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(priceNumber);
    
    return t('pricingSection.format.price', { price: formattedPrice });
  };

  const handlePriceChange = (e) => {
    const text = e.target.value;
    const cleaned = text.replace(/[^0-9]/g, '');
    setPriceInput(cleaned);
    
    onPricingChange({
      ...pricing,
      price: cleaned
    });
  };

  const handleNegotiableChange = (e) => {
    const checked = e.target.checked;
    setNegotiableActive(checked);
    onPricingChange({
      ...pricing,
      negotiable: checked
    });
  };

  const handleTradeChange = (e) => {
    const checked = e.target.checked;
    setTradeActive(checked);
    onPricingChange({
      ...pricing,
      acceptTrade: checked
    });
  };

  const isValidPrice = priceInput && parseFloat(priceInput) > 0;

  return (
    <div className="pricing-section">
      {/* En-tête */}
      <div className="pricing-header">
        <div className="header-content">
          <h1 className="header-title">{t('pricingSection.header.title')}</h1>
          <p className="header-subtitle">
            {t('pricingSection.header.subtitle')}
          </p>
        </div>
        {isEditMode && (
          <div className="edit-mode-badge">
            <Icon name="edit" size={14} color="#FFF" />
            <span className="edit-mode-text">{t('commonss.editMode')}</span>
          </div>
        )}
      </div>

      {/* Zone de saisie du prix */}
      <div className="input-section">
        <h2 className="section-label">{t('pricingSection.priceInput.label')}</h2>
        <p className="section-description">
          {t('pricingSection.priceInput.description')}
        </p>
        
        <div className="price-input-container">
          <div className="currency-badge">
            <Icon name="attach_money" size={20} color="#3B82F6" />
            <span className="currency-text">{t('pricingSection.currency')}</span>
          </div>
          
          <div className="price-input-wrapper">
            <input
              type="text"
              className="price-input"
              placeholder={t('pricingSection.priceInput.placeholder')}
              value={priceInput}
              onChange={handlePriceChange}
              inputMode="numeric"
            />
            <span className="currency-symbol">{t('pricingSection.currency')}</span>
          </div>
        </div>
        
        {isValidPrice && (
          <div className="price-preview">
            <Icon name="local_offer" size={16} color="#10B981" />
            <span className="price-preview-text">
              {t('pricingSection.pricePreview', { price: formatPrice(priceInput) })}
            </span>
          </div>
        )}
      </div>

      {/* Options de vente - VERSION CORRIGÉE */}
      <div className="options-section">
        <h2 className="section-label">{t('pricingSection.salesOptions.label')}</h2>
        <p className="section-description">
          {t('pricingSection.salesOptions.description')}
        </p>
        
        <div className="options-grid">
          {/* Option Prix Négociable */}
          <label className="option-label">
            <input
              type="checkbox"
              className="option-checkbox"
              checked={negotiableActive}
              onChange={handleNegotiableChange}
              style={{ display: 'none' }}
            />
            <div className={`option-card ${negotiableActive ? 'active' : ''}`}>
              <div className="option-icon-container">
                <Icon 
                  name="handshake" 
                  size={24} 
                  color={negotiableActive ? "#3B82F6" : "#6B7280"} 
                />
              </div>
              <div className="option-content">
                <h3 className={`option-title ${negotiableActive ? 'active' : ''}`}>
                  {t('pricingSection.options.negotiable.title')}
                </h3>
                <p className="option-description">
                  {t('pricingSection.options.negotiable.description')}
                </p>
              </div>
              <div className="option-checkbox-display">
                {negotiableActive ? (
                  <Icon name="check_circle" size={24} color="#3B82F6" />
                ) : (
                  <Icon name="radio_button_unchecked" size={24} color="#D1D5DB" />
                )}
              </div>
            </div>
          </label>
          
          {/* Option Échange */}
          <label className="option-label">
            <input
              type="checkbox"
              className="option-checkbox"
              checked={tradeActive}
              onChange={handleTradeChange}
              style={{ display: 'none' }}
            />
            <div className={`option-card ${tradeActive ? 'active' : ''}`}>
              <div className="option-icon-container">
                <Icon 
                  name="swap_horiz" 
                  size={24} 
                  color={tradeActive ? "#3B82F6" : "#6B7280"} 
                />
              </div>
              <div className="option-content">
                <h3 className={`option-title ${tradeActive ? 'active' : ''}`}>
                  {t('pricingSection.options.trade.title')}
                </h3>
                <p className="option-description">
                  {t('pricingSection.options.trade.description')}
                </p>
              </div>
              <div className="option-checkbox-display">
                {tradeActive ? (
                  <Icon name="check_circle" size={24} color="#3B82F6" />
                ) : (
                  <Icon name="radio_button_unchecked" size={24} color="#D1D5DB" />
                )}
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Aperçu du prix */}
      {isValidPrice && (
        <div className="preview-section">
          <div className="preview-header">
            <Icon name="visibility" size={20} color="#6B7280" />
            <h3 className="preview-title">{t('pricingSection.preview.title')}</h3>
          </div>
          
          <div className="preview-card">
            <div className="preview-price-display">
              <span className="preview-price-label">{t('pricingSection.preview.priceLabel')}</span>
              <span className="preview-price-value">
                {formatPrice(priceInput)}
              </span>
            </div>
            
            <div className="preview-badges">
              {negotiableActive && (
                <div className="badge">
                  <Icon name="handshake" size={12} color="#FFF" />
                  <span className="badge-text">{t('pricingSection.badges.negotiable')}</span>
                </div>
              )}
              {tradeActive && (
                <div className="badge badge-trade">
                  <Icon name="swap_horiz" size={12} color="#FFF" />
                  <span className="badge-text">{t('pricingSection.badges.trade')}</span>
                </div>
              )}
            </div>
            
            {/* Indication de prix final */}
            <div className="price-info">
              <Icon name="info" size={16} color="#6B7280" />
              <p className="price-info-text">
                {t('pricingSection.preview.info')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Conseils */}
      <div className="tips-section">
        <div className="tips-header">
          <Icon name="lightbulb_outline" size={20} color="#F59E0B" />
          <h3 className="tips-title">{t('pricingSection.tips.title')}</h3>
        </div>
        
        <div className="tips-list">
          <div className="tip-item">
            <div className="tip-icon">
              <Icon name="trending_up" size={16} color="#059669" />
            </div>
            <p className="tip-text">
              {t('pricingSection.tips.compare')}
            </p>
          </div>
          
          <div className="tip-item">
            <div className="tip-icon">
              <Icon name="favorite" size={16} color="#059669" />
            </div>
            <p className="tip-text">
              {t('pricingSection.tips.negotiable')}
            </p>
          </div>
          
          <div className="tip-item">
            <div className="tip-icon">
              <Icon name="attach_money" size={16} color="#059669" />
            </div>
            <p className="tip-text">
              {t('pricingSection.tips.trade')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingSection;